import {
	Copy,
	Download,
	ExternalLink,
	Globe,
	Key,
	Lock,
	Plus,
	Reply,
	Trash2,
	X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useReactFlow } from 'reactflow';
import { useIsDark } from '../../contexts/ThemeContext';
import {
	httpEndpointApiKeysClient,
	type HttpEndpointApiKey,
} from '../../lib/http-endpoint-api-keys';
import type { IntegrationsNodeData } from '../../types/nodes';

/**
 * Configuration interface for HTTP endpoint trigger
 */
interface StartHttpEndpointConfigType {
	slug?: string;
	timeoutMs?: number;
	description?: string;
	inputSchema?: {
		type: 'simple' | 'custom';
		fields?: Array<{
			name: string;
			type: string;
			required?: boolean;
			description?: string;
		}>;
		customJson?: string;
	};
	auth?: {
		requiredScopes?: string[];
	};
	metadata?: {
		title: string;
		description: string;
		source: string;
	};
}

/**
 * Props interface for HTTP endpoint configuration component
 */
interface StartHttpEndpointConfigProps {
	nodeId: string;
	data: IntegrationsNodeData;
	onChange: (data: IntegrationsNodeData) => void;
	onWizardStateChange?: (state: {
		wizardMode: boolean;
		currentStep: number;
		totalSteps: number;
		canProceedToNext: boolean;
		onPreviousStep?: () => void;
		onNextStep?: () => void;
		onCompleteWizard?: () => void;
		validationAttempted: boolean;
		validationFailed: boolean;
	}) => void;
}

const defaultConfig: StartHttpEndpointConfigType = {
	slug: '',
	timeoutMs: 30000,
	description: 'HTTP API endpoint trigger with API key authentication',
	inputSchema: {
		type: 'simple',
		fields: [{ name: 'id', type: 'string', required: true, description: 'Record identifier' }],
	},
	auth: {
		requiredScopes: ['flows:execute'],
	},
	metadata: {
		title: 'HTTP Endpoint Trigger',
		description: 'Executes workflow via API key authenticated HTTP endpoint',
		source: 'http-endpoint',
	},
};

/**
 * Configuration component for HTTP endpoint trigger nodes
 */
export function StartHttpEndpointConfig({
	nodeId,
	data,
	onChange,
	onWizardStateChange,
}: StartHttpEndpointConfigProps) {
	const [activeTab, setActiveTab] = useState<'basic' | 'security' | 'examples'>('basic');
	const [copiedItem, setCopiedItem] = useState<string | null>(null);
	const [wizardStep, setWizardStep] = useState(1);
	const [isWizardMode, setIsWizardMode] = useState(true);
	const [showApiKeyDialog, setShowApiKeyDialog] = useState(false);
	const [generatedApiKey, setGeneratedApiKey] = useState<{
		keyId: string;
		secret: string;
	} | null>(null);
	const [apiKeys, setApiKeys] = useState<HttpEndpointApiKey[]>([]);
	const [isLoadingApiKeys, setIsLoadingApiKeys] = useState(false);
	const [apiKeyName, setApiKeyName] = useState('HTTP Endpoint Key');
	const isDark = useIsDark();
	const { getNodes } = useReactFlow();

	// Initialize config with defaults
	const config: StartHttpEndpointConfigType = {
		...defaultConfig,
		...data.config,
		auth: {
			...defaultConfig.auth,
			...data.config?.auth,
		},
		metadata: {
			...defaultConfig.metadata,
			...data.config?.metadata,
		},
	};

	// Auto-detect response nodes in the workflow
	const allNodes = getNodes();
	const responseNodes = allNodes.filter(node => node.type === 'response');

	// Load API keys on component mount
	useEffect(() => {
		loadApiKeys();
	}, []);

	/**
	 * Load API keys from the backend
	 */
	const loadApiKeys = async () => {
		setIsLoadingApiKeys(true);
		try {
			const keys = await httpEndpointApiKeysClient.getApiKeys();
			setApiKeys(keys);
		} catch (error) {
			console.error('Failed to load API keys:', error);
		} finally {
			setIsLoadingApiKeys(false);
		}
	};

	/**
	 * Create a new API key
	 */
	const createApiKey = async () => {
		try {
			const result = await httpEndpointApiKeysClient.createApiKey({
				name: apiKeyName,
				description: `API key for HTTP endpoint: ${config.slug || 'endpoint'}`,
				scopes: ['flows:execute'],
				rateLimitRpm: 60,
				rateLimitRph: 1000,
			});

			setGeneratedApiKey({
				keyId: result.apiKey.keyId,
				secret: result.plainSecret,
			});
			setShowApiKeyDialog(true);

			// Reload the API keys list
			await loadApiKeys();
		} catch (error) {
			console.error('Failed to create API key:', error);
			alert('Failed to create API key. Please try again.');
		}
	};

	/**
	 * Deactivate an API key
	 */
	const deactivateApiKey = async (keyId: string) => {
		if (
			!confirm(
				'Are you sure you want to deactivate this API key? This action cannot be undone.'
			)
		) {
			return;
		}

		try {
			await httpEndpointApiKeysClient.deactivateApiKey(keyId);
			await loadApiKeys(); // Reload the list
		} catch (error) {
			console.error('Failed to deactivate API key:', error);
			alert('Failed to deactivate API key. Please try again.');
		}
	};

	// Communicate wizard state to parent
	useEffect(() => {
		if (onWizardStateChange) {
			onWizardStateChange({
				wizardMode: isWizardMode,
				currentStep: wizardStep,
				totalSteps: 2,
				canProceedToNext: wizardStep < 2 && validateSlug(config.slug || '').valid,
				onPreviousStep: () => {
					if (wizardStep > 1) setWizardStep(wizardStep - 1);
				},
				onNextStep: () => {
					if (wizardStep < 2) setWizardStep(wizardStep + 1);
				},
				onCompleteWizard: () => setIsWizardMode(false),
				validationAttempted: false,
				validationFailed: !validateSlug(config.slug || '').valid,
			});
		}
	}, [isWizardMode, wizardStep, config.slug, onWizardStateChange]);

	const handleConfigChange = (updates: Partial<StartHttpEndpointConfigType>) => {
		const newConfig = { ...config, ...updates };
		onChange({ ...data, config: newConfig });
	};

	const generateSlugFromWorkflow = () => {
		// Use configured slug if available
		if (config.slug && config.slug.trim()) {
			return config.slug.trim();
		}

		// Fallback to workflow metadata
		const workflowMetadata = (window as any).workflowContext?.metadata;
		if (workflowMetadata?.slug) return workflowMetadata.slug;
		if (workflowMetadata?.title || workflowMetadata?.name) {
			const name = workflowMetadata.title || workflowMetadata.name;
			return (
				name
					.toLowerCase()
					.replace(/[^a-z0-9\s-]/g, '')
					.replace(/\s+/g, '-')
					.replace(/-+/g, '-')
					.replace(/^-|-$/g, '') || 'your-workflow-slug'
			);
		}
		return 'your-workflow-slug';
	};

	const validateSlug = (slug: string): { valid: boolean; error?: string } => {
		if (!slug || slug.trim() === '') {
			return { valid: false, error: 'Slug is required' };
		}

		const cleanSlug = slug.trim();

		// Check format
		if (!/^[a-z0-9-]+$/.test(cleanSlug)) {
			return {
				valid: false,
				error: 'Slug can only contain lowercase letters, numbers, and hyphens',
			};
		}

		// Check length
		if (cleanSlug.length < 3) {
			return { valid: false, error: 'Slug must be at least 3 characters long' };
		}

		if (cleanSlug.length > 80) {
			return { valid: false, error: 'Slug must be 80 characters or less' };
		}

		// Check start/end
		if (cleanSlug.startsWith('-') || cleanSlug.endsWith('-')) {
			return { valid: false, error: 'Slug cannot start or end with a hyphen' };
		}

		// Check for double hyphens
		if (cleanSlug.includes('--')) {
			return { valid: false, error: 'Slug cannot contain consecutive hyphens' };
		}

		return { valid: true };
	};

	const generateApiEndpointUrl = () => {
		const baseUrl = import.meta.env.VITE_API_URL || 'https://api.yourhost.com';
		const slug = generateSlugFromWorkflow();
		return `${baseUrl}/api/v1/flows/${slug}/execute`;
	};

	const generateCurlExample = () => {
		const url = generateApiEndpointUrl();
		const body = JSON.stringify({
			input: { example: 'data' },
		});

		return `curl -X POST "${url}" \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: <YOUR_API_KEY>" \\
  -d '${body}'`;
	};

	const generateTypeScriptExample = () => {
		return `// TypeScript example for HTTP endpoint
const response = await fetch('${generateApiEndpointUrl()}', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': 'YOUR_API_KEY',
  },
  body: JSON.stringify({
    input: { example: 'data' }
  })
});`;
	};

	const copyToClipboard = async (text: string, item: string) => {
		try {
			await navigator.clipboard.writeText(text);
			setCopiedItem(item);
			setTimeout(() => setCopiedItem(null), 2000);
		} catch (error) {
			console.error('Failed to copy to clipboard:', error);
		}
	};

	const wizardSteps = [
		{
			number: 1,
			title: 'Basic Setup',
			icon: Globe,
			description: 'Configure endpoint settings',
		},
		{
			number: 2,
			title: 'Ready to Use',
			icon: Lock,
			description: 'View endpoint details and examples',
		},
	];

	return (
		<div className="space-y-6">
			{/* Wizard Header */}
			{isWizardMode && (
				<div className="pb-4 mb-6 border-b border-gray-200 dark:border-gray-700">
					<div className="mb-4">
						<h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
							HTTP Endpoint Setup Wizard
						</h2>
					</div>

					{/* Wizard Progress */}
					<div className="flex items-center space-x-4">
						{wizardSteps.map((step, index) => {
							const isActive = wizardStep === step.number;
							const isCompleted = wizardStep > step.number;

							return (
								<div key={step.number} className="flex items-center">
									<div
										className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
											isCompleted
												? 'text-white bg-green-500'
												: isActive
												? 'text-white bg-blue-600'
												: 'text-gray-600 bg-gray-200 dark:bg-gray-700 dark:text-gray-300'
										}`}
									>
										{isCompleted ? '✓' : step.number}
									</div>
									<div className="ml-2">
										<p
											className={`text-sm font-medium ${
												isActive
													? 'text-blue-600 dark:text-blue-400'
													: 'text-gray-600 dark:text-gray-400'
											}`}
										>
											{step.title}
										</p>
										<p className="text-xs text-gray-500">{step.description}</p>
									</div>
									{index < wizardSteps.length - 1 && (
										<div
											className={`w-8 h-px mx-4 ${
												isCompleted
													? 'bg-green-500'
													: 'bg-gray-300 dark:bg-gray-600'
											}`}
										/>
									)}
								</div>
							);
						})}
					</div>
				</div>
			)}

			{/* Step 1: Basic Setup */}
			{isWizardMode && wizardStep === 1 && (
				<div className="space-y-6">
					<div className="text-center">
						<Globe className="mx-auto mb-4 w-12 h-12 text-blue-500" />
						<h3 className="mb-2 text-xl font-semibold text-gray-900 dark:text-gray-100">
							HTTP Endpoint Setup
						</h3>
						<p className="mx-auto max-w-md text-sm text-gray-600 dark:text-gray-400">
							Configure basic settings for your HTTP API endpoint.
						</p>
					</div>

					<div className="p-6 space-y-6 bg-gray-50 rounded-lg dark:bg-gray-800">
						{/* Slug Configuration */}
						<div>
							<label className="block mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
								API Slug <span className="text-red-500">*</span>
							</label>
							<div className="space-y-2">
								<input
									type="text"
									value={config.slug || ''}
									onChange={e => {
										const newSlug = e.target.value
											.toLowerCase()
											.replace(/[^a-z0-9-]/g, '');
										handleConfigChange({ slug: newSlug });
									}}
									placeholder="my-api-endpoint"
									className="px-3 py-2 w-full font-mono text-sm text-gray-900 bg-white rounded-md border border-gray-300 shadow-sm dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
								/>
								{config.slug &&
									(() => {
										const validation = validateSlug(config.slug);
										return !validation.valid ? (
											<p className="text-xs text-red-600 dark:text-red-400">
												{validation.error}
											</p>
										) : (
											<p className="text-xs text-green-600 dark:text-green-400">
												✓ Valid slug format
											</p>
										);
									})()}
								<p className="text-xs text-gray-500 dark:text-gray-400">
									URL-friendly identifier for your API endpoint (lowercase,
									numbers, hyphens only)
								</p>
							</div>
						</div>

						{/* Timeout Configuration */}
						<div>
							<label className="block mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
								Execution Timeout
							</label>
							<div className="flex items-center space-x-3">
								<input
									type="range"
									min="5"
									max="300"
									value={(config.timeoutMs || 30000) / 1000}
									onChange={e =>
										handleConfigChange({
											timeoutMs: parseInt(e.target.value) * 1000,
										})
									}
									className="flex-1"
								/>
								<div className="text-right min-w-[80px]">
									<span className="text-lg font-medium text-gray-900 dark:text-gray-100">
										{(config.timeoutMs || 30000) / 1000}s
									</span>
								</div>
							</div>
							<div className="flex justify-between mt-1 text-xs text-gray-500 dark:text-gray-400">
								<span>5s (Quick)</span>
								<span>300s (Long-running)</span>
							</div>
						</div>

						{/* Input Schema */}
						<div>
							<label className="block mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
								Expected Input Data
							</label>
							<div className="space-y-3">
								{/* Schema Type Selection */}
								<div className="flex gap-2">
									<button
										onClick={() =>
											handleConfigChange({
												inputSchema: {
													...config.inputSchema,
													type: 'simple',
												},
											})
										}
										className={`px-3 py-2 text-sm rounded border transition-colors ${
											config.inputSchema?.type === 'simple'
												? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300'
												: 'border-gray-300 dark:border-gray-600 hover:border-blue-300'
										}`}
									>
										Simple Fields
									</button>
									<button
										onClick={() =>
											handleConfigChange({
												inputSchema: {
													...config.inputSchema,
													type: 'custom',
												},
											})
										}
										className={`px-3 py-2 text-sm rounded border transition-colors ${
											config.inputSchema?.type === 'custom'
												? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300'
												: 'border-gray-300 dark:border-gray-600 hover:border-blue-300'
										}`}
									>
										Custom JSON
									</button>
								</div>

								{/* Simple Fields Editor */}
								{config.inputSchema?.type === 'simple' && (
									<div className="space-y-2">
										{(config.inputSchema.fields || []).map((field, index) => (
											<div key={index} className="flex gap-2 items-center">
												<input
													type="text"
													value={field.name}
													onChange={e => {
														const updatedFields = [
															...(config.inputSchema?.fields || []),
														];
														updatedFields[index] = {
															...field,
															name: e.target.value,
														};
														handleConfigChange({
															inputSchema: {
																type: 'simple',
																...config.inputSchema,
																fields: updatedFields,
															},
														});
													}}
													placeholder="Field name"
													className="flex-1 px-3 py-2 text-sm bg-white rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
												/>
												<select
													value={field.type}
													onChange={e => {
														const updatedFields = [
															...(config.inputSchema?.fields || []),
														];
														updatedFields[index] = {
															...field,
															type: e.target.value,
														};
														handleConfigChange({
															inputSchema: {
																type: 'simple',
																...config.inputSchema,
																fields: updatedFields,
															},
														});
													}}
													className="px-3 py-2 text-sm bg-white rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
												>
													<option value="string">String</option>
													<option value="number">Number</option>
													<option value="boolean">Boolean</option>
													<option value="array">Array</option>
													<option value="object">Object</option>
												</select>
												<button
													onClick={() => {
														const updatedFields = [
															...(config.inputSchema?.fields || []),
														];
														updatedFields.splice(index, 1);
														handleConfigChange({
															inputSchema: {
																type: 'simple',
																...config.inputSchema,
																fields: updatedFields,
															},
														});
													}}
													className="px-2 py-2 text-red-600 hover:text-red-700"
												>
													×
												</button>
											</div>
										))}
										<button
											onClick={() => {
												const updatedFields = [
													...(config.inputSchema?.fields || []),
												];
												updatedFields.push({
													name: '',
													type: 'string',
													required: false,
												});
												handleConfigChange({
													inputSchema: {
														type: 'simple',
														...config.inputSchema,
														fields: updatedFields,
													},
												});
											}}
											className="px-3 py-2 text-sm text-blue-600 rounded border border-blue-300 border-dashed hover:text-blue-700"
										>
											+ Add Field
										</button>
									</div>
								)}

								{/* Custom JSON Editor */}
								{config.inputSchema?.type === 'custom' && (
									<div>
										<textarea
											value={config.inputSchema.customJson || ''}
											onChange={e =>
												handleConfigChange({
													inputSchema: {
														type: 'custom',
														...config.inputSchema,
														customJson: e.target.value,
													},
												})
											}
											placeholder={`{
  "userId": "string",
  "status": "string",
  "metadata": "object"
}`}
											rows={6}
											className="px-3 py-2 w-full font-mono text-sm bg-white rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
										/>
										<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
											Define the expected JSON structure for requests
										</p>
									</div>
								)}

								{/* Preview */}
								<div className="p-3 bg-gray-50 rounded dark:bg-gray-800">
									<h5 className="mb-2 text-sm font-medium">
										Expected Request Format:
									</h5>
									<pre className="font-mono text-xs text-gray-700 dark:text-gray-300">
										{config.inputSchema?.type === 'simple'
											? JSON.stringify(
													{
														input: Object.fromEntries(
															(config.inputSchema.fields || []).map(
																f => [
																	f.name || 'fieldName',
																	f.type === 'string'
																		? 'example'
																		: f.type === 'number'
																		? 123
																		: f.type === 'boolean'
																		? true
																		: f.type === 'array'
																		? f.name
																				?.toLowerCase()
																				.includes('id')
																			? [123, 456, 789]
																			: f.name
																					?.toLowerCase()
																					.includes(
																						'status'
																					)
																			? [
																					'active',
																					'pending',
																					'verified',
																			  ]
																			: [1, 2, 3]
																		: {},
																]
															)
														),
													},
													null,
													2
											  )
											: config.inputSchema?.customJson ||
											  '{ "input": { ... } }'}
									</pre>
								</div>
							</div>
						</div>

						{/* Description */}
						<div>
							<label className="block mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
								Description (Optional)
							</label>
							<textarea
								value={config.description || ''}
								onChange={e => handleConfigChange({ description: e.target.value })}
								placeholder="Describe what this API endpoint does..."
								rows={3}
								className="px-3 py-2 w-full text-gray-900 bg-white rounded-md border border-gray-300 shadow-sm dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
							/>
						</div>

						{/* Endpoint Preview */}
						<div className="p-4 bg-blue-50 rounded-lg border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
							<h4 className="mb-2 text-sm font-medium text-blue-900 dark:text-blue-100">
								API Endpoint Preview
							</h4>
							<div className="px-3 py-2 font-mono text-sm text-blue-800 bg-blue-100 rounded border dark:text-blue-200 dark:bg-blue-900/40">
								POST /api/flows/{generateSlugFromWorkflow()}/execute
							</div>
							<div className="flex justify-between items-center mt-2">
								<p className="text-xs text-blue-700 dark:text-blue-300">
									{config.slug
										? 'Using custom slug'
										: 'Using auto-generated slug'}
								</p>
								<button
									onClick={() =>
										copyToClipboard(generateApiEndpointUrl(), 'endpoint-url')
									}
									className="flex gap-1 items-center px-2 py-1 text-xs text-white bg-blue-600 rounded transition-colors hover:bg-blue-700"
								>
									<Copy className="w-3 h-3" />
									{copiedItem === 'endpoint-url' ? 'Copied!' : 'Copy URL'}
								</button>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Step 2: Ready to Use */}
			{isWizardMode && wizardStep === 2 && (
				<div className="space-y-6">
					<div className="text-center">
						<Lock className="mx-auto mb-4 w-12 h-12 text-green-500" />
						<h3 className="mb-2 text-xl font-semibold text-gray-900 dark:text-gray-100">
							Your API Endpoint is Ready!
						</h3>
						<p className="mx-auto max-w-md text-sm text-gray-600 dark:text-gray-400">
							Your HTTP endpoint is configured with enterprise-grade security.
						</p>
					</div>

					{/* Response Node Status */}
					<div className="p-6 text-center bg-green-50 rounded-lg border border-green-200 dark:bg-green-900/20 dark:border-green-800">
						<Reply className="mx-auto mb-4 w-12 h-12 text-green-500" />
						<h4 className="mb-3 text-xl font-medium text-green-900 dark:text-green-100">
							Response Nodes
						</h4>
						<p className="mb-4 text-green-800 dark:text-green-200">
							{responseNodes.length > 0
								? `✅ Found ${responseNodes.length} Response node(s) - responses will be automatically formatted`
								: '⚠️ Add a Response node to format your API responses'}
						</p>

						{responseNodes.length === 0 && (
							<div className="p-4 bg-white rounded-lg dark:bg-gray-800">
								<p className="text-sm text-gray-700 dark:text-gray-300">
									💡 Add a Response node from the Flow Control section to format
									API responses
								</p>
							</div>
						)}
					</div>

					{/* API Key Setup */}
					<div className="p-6 bg-yellow-50 rounded-lg border border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800">
						<h4 className="flex items-center mb-4 text-lg font-medium text-yellow-900 dark:text-yellow-100">
							<Key className="mr-2 w-5 h-5" />
							Create API Key
						</h4>

						<div className="space-y-4">
							<p className="text-sm text-yellow-800 dark:text-yellow-200">
								Your HTTP endpoint requires API key authentication. Create one now:
							</p>

							<div className="p-4 space-y-3 bg-white rounded-lg dark:bg-gray-800">
								<div className="grid grid-cols-2 gap-4">
									<div>
										<label className="block mb-1 text-xs font-medium text-gray-700 dark:text-gray-300">
											API Key Name
										</label>
										<input
											type="text"
											value={apiKeyName}
											onChange={e => setApiKeyName(e.target.value)}
											placeholder="My HTTP Endpoint Key"
											className="px-3 py-2 w-full text-sm bg-white rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
										/>
									</div>
									<div>
										<label className="block mb-1 text-xs font-medium text-gray-700 dark:text-gray-300">
											Rate Limit (req/min)
										</label>
										<input
											type="number"
											defaultValue={60}
											min={1}
											max={1000}
											className="px-3 py-2 w-full text-sm rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
										/>
									</div>
								</div>

								<button
									onClick={createApiKey}
									className="flex gap-2 justify-center items-center px-4 py-2 w-full text-white bg-green-600 rounded transition-colors hover:bg-green-700"
								>
									<Plus className="w-4 h-4" />
									Generate API Key
								</button>
							</div>

							{/* Existing API Keys List */}
							{apiKeys.length > 0 && (
								<div className="mt-4">
									<h4 className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
										Existing API Keys
									</h4>
									<div className="space-y-2">
										{apiKeys.map(key => (
											<div
												key={key.id}
												className="flex justify-between items-center p-3 bg-gray-50 rounded-lg dark:bg-gray-700"
											>
												<div className="flex-1">
													<div className="flex gap-2 items-center">
														<code className="font-mono text-sm text-gray-800 dark:text-gray-200">
															{key.keyId}
														</code>
														<span
															className={`px-2 py-1 text-xs rounded ${
																key.isActive
																	? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
																	: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
															}`}
														>
															{key.isActive ? 'Active' : 'Inactive'}
														</span>
													</div>
													<p className="text-xs text-gray-600 dark:text-gray-400">
														{key.name} • {key.rateLimitRpm} req/min •
														Created{' '}
														{new Date(
															key.createdAt
														).toLocaleDateString()}
													</p>
												</div>
												<div className="flex gap-2">
													<button
														onClick={() =>
															copyToClipboard(
																key.keyId,
																`key-${key.id}`
															)
														}
														className="p-2 text-gray-600 rounded hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-600"
														title="Copy API Key"
													>
														<Copy className="w-4 h-4" />
													</button>
													<button
														onClick={() => deactivateApiKey(key.keyId)}
														className="p-2 text-red-600 rounded hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900/20"
														title="Deactivate API Key"
													>
														<Trash2 className="w-4 h-4" />
													</button>
												</div>
											</div>
										))}
									</div>
								</div>
							)}

							<div className="p-3 bg-blue-50 rounded-lg dark:bg-blue-900/20">
								<p className="text-xs text-blue-800 dark:text-blue-200">
									💡 <strong>Security:</strong> API keys provide secure access to
									your HTTP endpoints. Store them securely and rotate them
									regularly.
								</p>
							</div>
						</div>
					</div>

					{/* Endpoint Summary */}
					<div className="p-6 bg-blue-50 rounded-lg border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
						<h4 className="mb-4 text-lg font-medium text-blue-900 dark:text-blue-100">
							📡 Your API Endpoint
						</h4>
						<div className="space-y-3">
							<div>
								<span className="text-sm font-medium text-blue-800 dark:text-blue-200">
									URL:{' '}
								</span>
								<div className="px-3 py-2 mt-1 font-mono text-sm text-blue-700 bg-blue-100 rounded dark:text-blue-300 dark:bg-blue-900/40">
									POST {generateApiEndpointUrl()}
								</div>
							</div>
							<div>
								<span className="text-sm font-medium text-blue-800 dark:text-blue-200">
									Timeout:{' '}
								</span>
								<span className="text-sm text-blue-700 dark:text-blue-300">
									{(config.timeoutMs || 30000) / 1000}s
								</span>
							</div>
						</div>
					</div>

					{/* Quick cURL Example */}
					<div className="p-4 bg-gray-50 rounded-lg dark:bg-gray-800">
						<div className="flex justify-between items-center mb-3">
							<h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
								Test Command
							</h4>
							<button
								onClick={() => copyToClipboard(generateCurlExample(), 'curl')}
								className="flex gap-2 items-center px-3 py-1 text-xs text-white bg-blue-600 rounded hover:bg-blue-700"
							>
								<Copy className="w-3 h-3" />
								{copiedItem === 'curl' ? 'Copied!' : 'Copy'}
							</button>
						</div>
						<pre className="overflow-x-auto p-3 text-xs text-gray-100 bg-gray-900 rounded">
							<code>{generateCurlExample()}</code>
						</pre>
					</div>
				</div>
			)}

			{/* Step 3: Ready to Use */}
			{isWizardMode && wizardStep === 3 && (
				<div className="space-y-6">
					<div className="text-center">
						<Lock className="mx-auto mb-4 w-12 h-12 text-green-500" />
						<h3 className="mb-2 text-xl font-semibold text-gray-900 dark:text-gray-100">
							Your API Endpoint is Ready!
						</h3>
						<p className="mx-auto max-w-md text-sm text-gray-600 dark:text-gray-400">
							Your HTTP endpoint is configured with enterprise-grade security.
						</p>
					</div>

					{/* Endpoint Summary */}
					<div className="p-6 bg-green-50 rounded-lg border border-green-200 dark:bg-green-900/20 dark:border-green-800">
						<h4 className="mb-4 text-lg font-medium text-green-900 dark:text-green-100">
							📡 Your API Endpoint
						</h4>
						<div className="space-y-3">
							<div>
								<span className="text-sm font-medium text-green-800 dark:text-green-200">
									URL:{' '}
								</span>
								<div className="px-3 py-2 mt-1 font-mono text-sm text-green-700 bg-green-100 rounded dark:text-green-300 dark:bg-green-900/40">
									POST {generateApiEndpointUrl()}
								</div>
							</div>
							<div>
								<span className="text-sm font-medium text-green-800 dark:text-green-200">
									Response Nodes:{' '}
								</span>
								<span className="text-sm text-green-700 dark:text-green-300">
									{responseNodes.length > 0
										? `${responseNodes.length} Response node(s) detected`
										: 'No Response nodes found - add one to format responses'}
								</span>
							</div>
							<div>
								<span className="text-sm font-medium text-green-800 dark:text-green-200">
									Timeout:{' '}
								</span>
								<span className="text-sm text-green-700 dark:text-green-300">
									{(config.timeoutMs || 30000) / 1000}s
								</span>
							</div>
						</div>
					</div>

					{/* Quick cURL Example */}
					<div className="p-4 bg-gray-50 rounded-lg dark:bg-gray-800">
						<div className="flex justify-between items-center mb-3">
							<h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
								Test Command
							</h4>
							<button
								onClick={() => copyToClipboard(generateCurlExample(), 'curl')}
								className="flex gap-2 items-center px-3 py-1 text-xs text-white bg-blue-600 rounded hover:bg-blue-700"
							>
								<Copy className="w-3 h-3" />
								{copiedItem === 'curl' ? 'Copied!' : 'Copy'}
							</button>
						</div>
						<pre className="overflow-x-auto p-3 text-xs text-gray-100 bg-gray-900 rounded">
							<code>{generateCurlExample()}</code>
						</pre>
					</div>
				</div>
			)}

			{/* Advanced Mode */}
			{!isWizardMode && (
				<div className="space-y-6">
					<div className="flex justify-between items-center">
						<h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
							HTTP Endpoint Configuration
						</h2>
						<button
							onClick={() => setIsWizardMode(true)}
							className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700"
						>
							Switch to Wizard Mode
						</button>
					</div>

					{/* Tab Navigation */}
					<div className="flex p-1 space-x-1 bg-gray-100 rounded-lg dark:bg-gray-800">
						{[
							{ id: 'basic', label: 'Configuration', icon: Globe },
							{ id: 'security', label: 'Security', icon: Lock },
							{ id: 'examples', label: 'Code Examples', icon: ExternalLink },
						].map(tab => {
							const Icon = tab.icon;
							return (
								<button
									key={tab.id}
									onClick={() => setActiveTab(tab.id as any)}
									className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
										activeTab === tab.id
											? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm'
											: 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
									}`}
								>
									<Icon className="w-4 h-4" />
									{tab.label}
								</button>
							);
						})}
					</div>

					{/* Basic Tab */}
					{activeTab === 'basic' && (
						<div className="space-y-6">
							{/* Response Node Selection */}
							<div className="space-y-3">
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
									Response Node <span className="text-red-500">*</span>
								</label>

								<div className="p-4 bg-blue-50 rounded-lg border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
									<p className="text-sm text-blue-800 dark:text-blue-200">
										💡 Response nodes are now auto-detected. Add Response nodes
										to your workflow and they'll be used automatically.
									</p>
								</div>
							</div>

							{/* Timeout */}
							<div className="space-y-3">
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
									Timeout (seconds)
								</label>
								<input
									type="number"
									value={(config.timeoutMs || 30000) / 1000}
									onChange={e =>
										handleConfigChange({
											timeoutMs: parseInt(e.target.value) * 1000,
										})
									}
									min="1"
									max="300"
									className="px-3 py-2 w-20 rounded-md border border-gray-300 dark:border-gray-600"
								/>
							</div>

							{/* Description */}
							<div className="space-y-3">
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
									Description
								</label>
								<textarea
									value={config.description || ''}
									onChange={e =>
										handleConfigChange({ description: e.target.value })
									}
									rows={3}
									className="px-3 py-2 w-full rounded-md border border-gray-300 dark:border-gray-600"
								/>
							</div>
						</div>
					)}

					{/* Security Tab */}
					{activeTab === 'security' && (
						<div>
							<h3 className="mb-4 text-lg font-semibold">Security Features</h3>
							<div className="p-4 bg-green-50 rounded-lg dark:bg-green-900/20">
								<h4 className="mb-2 font-medium text-green-900 dark:text-green-100">
									Enterprise Security Enabled
								</h4>
								<ul className="space-y-1 text-sm text-green-800 dark:text-green-200">
									<li>• API Key Authentication</li>
									<li>• HMAC-SHA256 Signature Verification</li>
									<li>• Nonce-based Replay Protection</li>
									<li>• Timestamp Validation</li>
									<li>• Rate Limiting</li>
								</ul>
							</div>
						</div>
					)}

					{/* Examples Tab */}
					{activeTab === 'examples' && (
						<div className="space-y-4">
							<div className="flex justify-between items-center">
								<h3 className="text-lg font-semibold">cURL Example</h3>
								<button
									onClick={() =>
										copyToClipboard(generateCurlExample(), 'curl-advanced')
									}
									className="flex gap-2 items-center px-3 py-1 text-xs text-white bg-blue-600 rounded hover:bg-blue-700"
								>
									<Copy className="w-3 h-3" />
									{copiedItem === 'curl-advanced' ? 'Copied!' : 'Copy'}
								</button>
							</div>
							<pre className="overflow-x-auto p-4 text-xs text-gray-100 bg-gray-900 rounded">
								<code>{generateCurlExample()}</code>
							</pre>
						</div>
					)}
				</div>
			)}

			{/* API Key Created Dialog */}
			{showApiKeyDialog && generatedApiKey && (
				<div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
					<div className="mx-4 w-full max-w-md bg-white rounded-lg shadow-xl dark:bg-gray-800">
						<div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
							<div className="flex gap-3 items-center">
								<div className="flex justify-center items-center w-10 h-10 bg-green-100 rounded-full dark:bg-green-900/30">
									<Key className="w-5 h-5 text-green-600 dark:text-green-400" />
								</div>
								<div>
									<h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
										API Key Created!
									</h3>
									<p className="text-sm text-gray-600 dark:text-gray-400">
										Save these credentials securely
									</p>
								</div>
							</div>
							<button
								onClick={() => setShowApiKeyDialog(false)}
								className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
							>
								<X className="w-5 h-5" />
							</button>
						</div>

						<div className="p-6 space-y-4">
							<div className="p-4 bg-red-50 rounded-lg border border-red-200 dark:bg-red-900/20 dark:border-red-800">
								<p className="text-sm font-medium text-red-800 dark:text-red-200">
									⚠️ IMPORTANT: Save the secret now - it won't be shown again!
								</p>
							</div>

							<div className="space-y-3">
								<div>
									<label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
										Key ID (Public)
									</label>
									<div className="flex gap-2 items-center">
										<code className="flex-1 px-3 py-2 font-mono text-sm text-gray-900 bg-gray-100 rounded dark:bg-gray-700 dark:text-gray-100">
											{generatedApiKey.keyId}
										</code>
										<button
											onClick={() =>
												copyToClipboard(generatedApiKey.keyId, 'keyId')
											}
											className="flex gap-1 items-center px-3 py-2 text-white bg-blue-600 rounded hover:bg-blue-700"
										>
											<Copy className="w-4 h-4" />
											{copiedItem === 'keyId' ? 'Copied!' : 'Copy'}
										</button>
									</div>
								</div>

								<div>
									<label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
										Secret Key (Private)
									</label>
									<div className="flex gap-2 items-center">
										<code className="flex-1 px-3 py-2 font-mono text-sm text-gray-900 break-all bg-gray-100 rounded dark:bg-gray-700 dark:text-gray-100">
											{generatedApiKey.secret}
										</code>
										<button
											onClick={() =>
												copyToClipboard(generatedApiKey.secret, 'secret')
											}
											className="flex gap-1 items-center px-3 py-2 text-white bg-blue-600 rounded hover:bg-blue-700"
										>
											<Copy className="w-4 h-4" />
											{copiedItem === 'secret' ? 'Copied!' : 'Copy'}
										</button>
									</div>
								</div>
							</div>

							<div className="flex gap-3 items-center pt-4">
								<button
									onClick={() => {
										const apiKeyData = {
											keyId: generatedApiKey.keyId,
											secret: generatedApiKey.secret,
											createdAt: new Date().toISOString(),
											scopes: ['flows:execute'],
											rateLimitRpm: 60,
											rateLimitRph: 1000,
											usage: 'Save this file securely and use the credentials for HTTP endpoint authentication',
										};

										const dataStr = JSON.stringify(apiKeyData, null, 2);
										const dataBlob = new Blob([dataStr], {
											type: 'application/json',
										});
										const url = URL.createObjectURL(dataBlob);
										const link = document.createElement('a');
										link.href = url;
										link.download = `api-key-${generatedApiKey.keyId}.json`;
										link.click();
										URL.revokeObjectURL(url);
									}}
									className="flex flex-1 gap-2 justify-center items-center px-4 py-2 text-white bg-gray-600 rounded transition-colors hover:bg-gray-700"
								>
									<Download className="w-4 h-4" />
									Download as JSON
								</button>

								<button
									onClick={() => {
										const credentials = `API Key ID: ${generatedApiKey.keyId}\nSecret: ${generatedApiKey.secret}`;
										copyToClipboard(credentials, 'credentials');
									}}
									className="flex flex-1 gap-2 justify-center items-center px-4 py-2 text-white bg-blue-600 rounded transition-colors hover:bg-blue-700"
								>
									<Copy className="w-4 h-4" />
									{copiedItem === 'credentials' ? 'Copied!' : 'Copy Both'}
								</button>
							</div>

							<div className="pt-2 text-xs text-center text-gray-500 dark:text-gray-400">
								Use the Key ID in x-api-key header for authentication
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
